import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Alert from '../common/Alert.js';
import shipmentClientService from '../../services/ShipmentClientService.js';
import { Users } from 'lucide-react';
import { PageLayout, FormLayout, FormField, DetailCard } from '../common/index.js';

const EditShipmentClient = () => {
  const { clientId } = useParams();
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    contact: '',
    address: '',
    company_name: '',
    gst_number: '',
    status: 'active',
  });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const statusOptions = ['active', 'inactive'];

  useEffect(() => {
    fetchClient();
  }, [clientId]);

  const fetchClient = async () => {
    try {
      const response = await shipmentClientService.getById(clientId);
      setFormData(response.data);
    } catch (err) {
      Alert.error('Failed to load client data');
      console.error(err);
      navigate('/shipment-clients');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);

    try {
      await shipmentClientService.update(clientId, formData);
      Alert.success('Shipment client updated successfully!');
      setTimeout(() => navigate('/shipment-clients'), 1500);
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to update shipment client');
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass =
    'w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base';

  if (loading) {
    return (
      <PageLayout title="Edit Shipment Client" icon={Users}>
        <div className="flex items-center justify-center py-16">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand mx-auto mb-4" />
            <p className="text-sm text-gray-600">Loading client data...</p>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <FormLayout
      title="Edit Shipment Client"
      icon={Users}
      onBack={() => navigate('/shipment-clients')}
      backLabel="Back to Clients"
      onSubmit={handleSubmit}
      submitLabel="Update Client"
      onCancel={() => navigate('/shipment-clients')}
      loading={submitting}
    >
      <DetailCard title="Basic Information">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
          <FormField label="Full Name" required>
            <input
              type="text"
              name="full_name"
              value={formData.full_name}
              onChange={handleChange}
              className={inputClass}
              required
            />
          </FormField>
          <FormField label="Company Name" required>
            <input
              type="text"
              name="company_name"
              value={formData.company_name}
              onChange={handleChange}
              className={inputClass}
              required
            />
          </FormField>
          <FormField label="GST Number">
            <input
              type="text"
              name="gst_number"
              value={formData.gst_number}
              onChange={handleChange}
              maxLength="15"
              className={inputClass}
            />
          </FormField>
        </div>
      </DetailCard>

      <DetailCard title="Contact Information">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
          <FormField label="Email" required>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={inputClass}
              required
            />
          </FormField>
          <FormField label="Contact Number" required>
            <input
              type="text"
              name="contact"
              value={formData.contact}
              onChange={handleChange}
              className={inputClass}
              required
            />
          </FormField>
          <FormField label="Address" required className="sm:col-span-2">
            <textarea
              name="address"
              value={formData.address}
              onChange={handleChange}
              rows="3"
              className={inputClass}
              required
            />
          </FormField>
        </div>
      </DetailCard>

      <FormField label="Status">
        <select name="status" value={formData.status} onChange={handleChange} className={inputClass}>
          {statusOptions.map((status) => (
            <option key={status} value={status}>
              {status.toUpperCase()}
            </option>
          ))}
        </select>
      </FormField>
    </FormLayout>
  );
};

export default EditShipmentClient;
